import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, Phone, ArrowUpRight, Building2, Clock } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/content";
import { Reveal } from "@/components/Reveal";
import { WhatsAppIcon, whatsAppHref } from "@/components/WhatsAppFab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  company: "",
  phone: "",
  service: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  useEffect(() => window.scrollTo({ top: 0 }), []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we've received your note. A senior consultant will respond within one business day.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="page-contact">
      <section className="relative pt-40 pb-20 overflow-hidden border-b border-stone-200">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="absolute -top-32 left-1/4 w-[500px] h-[400px] glow-amber pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-6">
              Contact
            </div>
            <h1 className="font-display text-stone-900 text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter mb-6 max-w-5xl">
              Let's talk about what<br />
              <span className="italic text-[#B8860B]">you're solving for.</span>
            </h1>
            <p className="text-stone-600 text-lg md:text-xl max-w-2xl">
              No bots, no junior funnels. Your message lands directly with a senior consultant — typically with a response inside one business day.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="border border-stone-200 p-8 lg:p-10 bg-white">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Full name *">
                  <input
                    data-testid="contact-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="form-input"
                  />
                </Field>
                <Field label="Work email *">
                  <input
                    data-testid="contact-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="form-input"
                  />
                </Field>
                <Field label="Company">
                  <input
                    data-testid="contact-company"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="form-input"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    data-testid="contact-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="form-input"
                  />
                </Field>
              </div>

              <div className="mt-5">
                <Field label="What can we help with?">
                  <select
                    data-testid="contact-service"
                    value={form.service}
                    onChange={(e) => setForm({ ...form, service: e.target.value })}
                    className="form-input appearance-none"
                  >
                    <option value="" className="bg-white">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title} className="bg-white">{s.title}</option>
                    ))}
                    <option value="Other" className="bg-white">Something else</option>
                  </select>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Tell us about your project *">
                  <textarea
                    data-testid="contact-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="The challenge, your timeline, and what success looks like…"
                    className="form-input resize-none"
                  />
                </Field>
              </div>

              <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-stone-500 text-xs max-w-sm">
                  By submitting, you agree we may contact you about your enquiry. We don't share your details.
                </p>
                <button
                  data-testid="contact-submit"
                  disabled={loading}
                  type="submit"
                  className="btn-primary disabled:opacity-60"
                >
                  {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending</>) : (<>Send message <ArrowUpRight className="w-4 h-4" /></>)}
                </button>
              </div>
            </form>
          </Reveal>

          <Reveal className="lg:col-span-5 space-y-6">
            <a
              data-testid="contact-whatsapp-cta"
              href={whatsAppHref()}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between gap-4 p-6 bg-[#25D366] hover:bg-[#1DA851] text-white transition-colors group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                  <WhatsAppIcon className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] opacity-80 mb-1">Faster reply</div>
                  <div className="font-display text-xl leading-tight">Chat on WhatsApp</div>
                  <div className="text-sm opacity-90 mt-1">{COMPANY.whatsapp.display}</div>
                </div>
              </div>
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>

            <div className="border border-stone-200 p-8 bg-white">
              <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] mb-6">Headquarters</div>
              <div className="flex items-start gap-4 mb-5">
                <Building2 className="w-5 h-5 text-[#B8860B] mt-0.5" />
                <div className="text-stone-700">
                  {COMPANY.address.line1}<br />
                  {COMPANY.address.line2}<br />
                  {COMPANY.address.line3}<br />
                  {COMPANY.address.city}, {COMPANY.address.country}
                </div>
              </div>
              <div className="space-y-3">
                <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-3 text-stone-700 hover:text-stone-900">
                  <Phone className="w-4 h-4 text-[#B8860B]" /> {COMPANY.phone}
                </a>
                <a href={`mailto:${COMPANY.email}`} data-testid="contact-info-email" className="flex items-center gap-3 text-stone-700 hover:text-stone-900">
                  <Mail className="w-4 h-4 text-[#B8860B]" /> {COMPANY.email}
                </a>
                <a href={`mailto:${COMPANY.careers_email}`} className="flex items-center gap-3 text-stone-700 hover:text-stone-900">
                  <Mail className="w-4 h-4 text-[#B8860B]" /> {COMPANY.careers_email}
                </a>
              </div>
            </div>

            <div className="border border-stone-200 p-8 bg-white">
              <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] mb-6">Office hours</div>
              <div className="flex items-start gap-4 mb-4">
                <Clock className="w-5 h-5 text-[#B8860B] mt-0.5" />
                <div className="text-stone-700">
                  Sunday — Thursday<br />
                  09:00 — 18:00 GST
                </div>
              </div>
              <div className="text-stone-500 text-sm">
                Urgent? Mark your subject line with <span className="font-mono text-[#B8860B]">[urgent]</span> and we'll route within the hour.
              </div>
            </div>

            <div className="border border-stone-200 p-8 bg-white">
              <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] mb-6">Global delivery hubs</div>
              <div className="grid grid-cols-2 gap-3 text-stone-700">
                <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-[#B8860B]" /> Dubai</div>
                <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-[#B8860B]" /> Bengaluru</div>
                <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-[#B8860B]" /> Riyadh</div>
                <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-[#B8860B]" /> Lisbon</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">{label}</div>
    {children}
  </label>
);

export default Contact;
